import React, { useEffect, useMemo, useState } from "react";
import { AnalyticsViewConfigTable } from "@hypertune/shared-internal/src/types";
import {
  DbEvent,
  DbExposure,
  DbTableData,
  FunnelStep,
} from "@hypertune/shared-internal";
import { Split } from "@hypertune/sdk/src/shared";
import { CaretLeft, CaretRight, Pencil, Plus } from "@phosphor-icons/react";
import Skeleton from "react-loading-skeleton";
import { CommitContext, TimeRange } from "../../../lib/types";
import Filter from "./step/Filter";
import Label from "../../../components/Label";
import getFunnelStepPayloadObjectTypeName from "../../../lib/getFunnelStepPayloadObjectTypeName";
import {
  TableDataDocument,
  TableDataJsonInput,
  TableDataQuery,
  TableDataQueryVariables,
  useTableDataQuery,
} from "../../../generated/graphql";
import {
  intentPrimaryHex,
  queryPollIntervalVeryLongMs,
} from "../../../lib/constants";
import Table, { Column } from "../../../components/Table";
import { formatJsonTime } from "../../../lib/generic/date";
import Button from "../../../components/buttons/Button";
import AddOrEditStepModal from "./AddOrEditStepModal";
import { useAppDispatch } from "../../../app/hooks";
import { setConfig } from "./analyticsSlice";
import { getStepLabel } from "./step/utils";
import DeleteButton from "../../../components/buttons/DeleteButton";
import Box from "../../../components/Box";
import getApolloClient from "../../../lib/query/getApolloClient";
import getErrorMessage from "../../../lib/query/getErrorMessage";
import downloadCsv from "../../../lib/downloadCsv";

export default function AnalyticsTableView({
  meId,
  canEdit,
  commitContext,
  config,
  timeRange,
  selectedViewId,
}: {
  meId: string;
  canEdit: boolean;
  commitContext: CommitContext;
  config: AnalyticsViewConfigTable;
  timeRange: TimeRange;
  selectedViewId: string;
}): React.ReactElement {
  const dispatch = useAppDispatch();

  const limit = 100;

  const [offset, setOffset] = useState(0);

  const input: TableDataJsonInput = useMemo(
    () => ({
      start: timeRange.start.toJSON(),
      end: timeRange.end.toJSON(),
      viewId: selectedViewId,
      configJson: JSON.stringify(config),
      limit,
      offset,
    }),
    [timeRange.start, timeRange.end, selectedViewId, config, offset]
  );

  const { loading, error, data, startPolling, stopPolling } = useTableDataQuery(
    { variables: { input } }
  );

  useEffect(() => {
    startPolling(queryPollIntervalVeryLongMs);
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  const dbTableData = useMemo(
    () =>
      data
        ? // TODO: Validate JSON with zod
          (JSON.parse(data.tableDataJson) as DbTableData)
        : null,
    [data]
  );

  const hasExposures = useMemo(
    () => dbTableData?.rows.some(isExposure) ?? false,
    [dbTableData]
  );

  const columns = useMemo(
    () => getColumns(commitContext, hasExposures),
    [commitContext, hasExposures]
  );

  async function onExport(): Promise<void> {
    const tableData = await getTableData(timeRange, selectedViewId, config);

    if (!tableData) {
      return;
    }

    const cells = [
      columns.map((column) => column.label),
      ...tableData.rows.map((row) =>
        columns.map((column) => column.getCellContents(row))
      ),
    ];

    const fileName = `Results ${formatJsonTime(timeRange.start.toJSON(), /* skipAt */ true)} - ${formatJsonTime(timeRange.end.toJSON(), /* skipAt */ true)}`;

    downloadCsv(fileName, cells);
  }

  return dbTableData ? (
    <AnalyticsTable
      meId={meId}
      canEdit={canEdit}
      commitContext={commitContext}
      steps={config.steps}
      setSteps={(newSteps) => {
        dispatch(setConfig({ type: "table", steps: newSteps }));
      }}
      columns={columns}
      rows={dbTableData.rows}
      from={offset + 1}
      to={Math.min(offset + limit, dbTableData.count)}
      count={dbTableData.count}
      onExport={onExport}
      onPrevious={
        offset >= limit
          ? () => {
              setOffset(Math.max(0, offset - limit));
            }
          : undefined
      }
      onNext={
        offset + limit < dbTableData.count
          ? () => {
              setOffset(offset + limit);
            }
          : undefined
      }
    />
  ) : (
    <div className="h-full w-full overflow-auto rounded-[8px] p-4">
      <Skeleton className="h-[153px] w-full" />
      <div className="h-4" />
      <Skeleton className="h-[calc(100%-244px)] w-full" />
      <div className="h-4" />
      <Skeleton className="h-[48px] w-full" />
    </div>
  );
}

async function getTableData(
  timeRange: TimeRange,
  selectedViewId: string,
  analyticsViewConfigTable: AnalyticsViewConfigTable
): Promise<DbTableData | null> {
  try {
    const input: TableDataJsonInput = {
      start: timeRange.start.toJSON(),
      end: timeRange.end.toJSON(),
      viewId: selectedViewId,
      configJson: JSON.stringify(analyticsViewConfigTable),
      limit: 0,
      offset: 0,
    };

    const { data } = await getApolloClient().query<
      TableDataQuery,
      TableDataQueryVariables
    >({
      query: TableDataDocument,
      variables: { input },
      fetchPolicy: "network-only",
    });

    return JSON.parse(data.tableDataJson) as DbTableData;
  } catch (error) {
    console.error("[getTableData] error", {
      message: getErrorMessage(error),
      error,
    });
    return null;
  }
}

function AnalyticsTable({
  meId,
  canEdit,
  commitContext,
  steps,
  setSteps,
  columns,
  rows,
  from,
  to,
  count,
  onExport,
  onPrevious,
  onNext,
}: {
  meId: string;
  canEdit: boolean;
  commitContext: CommitContext;
  steps: FunnelStep[];
  setSteps: (newSteps: FunnelStep[]) => void;
  columns: Column<DbEvent | DbExposure>[];
  rows: (DbEvent | DbExposure)[];
  from: number;
  to: number;
  count: number;
  onExport: () => Promise<void>;
  onPrevious?: () => void;
  onNext?: () => void;
}): React.ReactElement {
  const [isAddingStep, setIsAddingStep] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-auto rounded-[8px] p-4">
      {canEdit && steps.length === 0 && (
        <div>
          <Button
            className="bg-white"
            intent="neutral"
            weight="outlined"
            size="large"
            icon={<Plus weight="regular" />}
            text="Add query"
            onClick={() => {
              setIsAddingStep(true);
            }}
          />
        </div>
      )}
      {steps.map((step, index) => (
        <Step
          key={`${index.toString()}`}
          meId={meId}
          canEdit={canEdit}
          commitContext={commitContext}
          steps={steps}
          setSteps={setSteps}
          index={index}
        />
      ))}
      <Table className="flex-grow" columns={columns} rows={rows} />
      <Box className="flex flex-row items-center gap-3 px-3 py-2">
        <div>
          Showing {from} to {to} of {count} rows
        </div>
        <div className="flex-grow" />
        <Button
          text="Export"
          weight="outlined"
          intent="neutral"
          onClick={() => {
            setIsExporting(true);
            onExport().finally(() => {
              setIsExporting(false);
            });
          }}
          loading={isExporting}
          loadingText="Exporting..."
        />
        <Button
          icon={<CaretLeft />}
          weight="outlined"
          intent="neutral"
          disabled={!onPrevious}
          onClick={onPrevious}
        />
        <Button
          icon={<CaretRight />}
          weight="outlined"
          intent="neutral"
          disabled={!onNext}
          onClick={onNext}
        />
      </Box>
      {isAddingStep && (
        <AddOrEditStepModal
          commitContext={commitContext}
          steps={steps}
          setSteps={setSteps}
          index={steps.length}
          onClose={() => setIsAddingStep(false)}
          insert
          analyticsViewConfigType="table"
        />
      )}
    </div>
  );
}

function Step({
  meId,
  canEdit,
  commitContext,
  steps,
  setSteps,
  index,
}: {
  meId: string;
  canEdit: boolean;
  commitContext: CommitContext;
  steps: FunnelStep[];
  setSteps: (newSteps: FunnelStep[]) => void;
  index: number;
}): React.ReactElement {
  const [isEditingStep, setIsEditingStep] = useState(false);

  const step = steps[index];

  function setStep(newStep: FunnelStep): void {
    setSteps([...steps.slice(0, index), newStep, ...steps.slice(index + 1)]);
  }

  return (
    <Box className="flex max-w-[300px] flex-col items-stretch gap-6 px-3 py-2">
      <div className="flex flex-col gap-3">
        <Label type="title4" className="font-semibold">
          {getStepLabel(commitContext, step)}
        </Label>
        <div className="flex flex-row gap-3">
          <DeleteButton
            className="flex-grow"
            onClick={() => {
              setSteps([...steps.slice(0, index), ...steps.slice(index + 1)]);
            }}
          />
          <Button
            className="flex-grow"
            icon={<Pencil color={intentPrimaryHex} />}
            weight="minimal"
            intent="primary"
            onClick={() => setIsEditingStep(true)}
          />
        </div>
        <Label type="title4" className="text-tx-muted">
          Filter
        </Label>
        <Filter
          meId={meId}
          stepType={step.type}
          payloadObjectTypeName={
            step
              ? getFunnelStepPayloadObjectTypeName(commitContext, step)
              : null
          }
          schema={commitContext.schema}
          filter={step.filter}
          setFilter={(newFilter) => setStep({ ...step, filter: newFilter })}
          canEdit={canEdit}
        />
      </div>
      {isEditingStep && (
        <AddOrEditStepModal
          commitContext={commitContext}
          steps={steps}
          setSteps={setSteps}
          index={index}
          insert={false}
          onClose={() => setIsEditingStep(false)}
          analyticsViewConfigType="table"
        />
      )}
    </Box>
  );
}

function getColumns(
  commitContext: CommitContext,
  hasExposures: boolean
): Column<DbEvent | DbExposure>[] {
  /**
   * Right now, we do some data transformation on the frontend, but long term
   * all the data transformation should happen on the backend, ideally in the
   * DB query itself, so that the frontend only receives the data it needs, and
   * there's no time-consuming work for the browser.
   */

  const columns: Column<DbEvent | DbExposure>[] = [];

  columns.push({
    label: "Created at",
    width: 150,
    getCellContents: (row) => formatJsonTime(row.created_at, /* skipAt */ true),
  });

  if (hasExposures) {
    columns.push({
      label: "Split name",
      width: 150,
      getCellContents: (row) =>
        isExposure(row) ? getSplitText(commitContext, row) : "",
    });

    columns.push({
      label: "Unit ID",
      width: 200,
      getCellContents: (row) => (isExposure(row) ? row.unit_id : ""),
    });

    columns.push({
      label: "Assignment",
      width: 150,
      getCellContents: (row) =>
        isExposure(row) ? getAssignmentText(commitContext, row) : "",
    });
  }

  columns.push({
    label: "Event type",
    width: 150,
    getCellContents: (row) => row.event_object_type_name ?? "",
  });

  columns.push({
    label: "Event payload",
    width: 350,
    getCellContents: (row) =>
      row.event_payload ? JSON.stringify(row.event_payload) : "",
  });

  return columns;
}

function isExposure(row: DbEvent | DbExposure): row is DbExposure {
  return "split_id" in row;
}

function getSplit(
  commitContext: CommitContext,
  row: DbExposure
): Split | undefined {
  return commitContext.splits[row.split_id] ?? undefined;
}

function getSplitText(commitContext: CommitContext, row: DbExposure): string {
  const split = getSplit(commitContext, row);

  if (!split) {
    return row.split_id;
  }

  return split.name;
}

function getAssignmentText(
  commitContext: CommitContext,
  row: DbExposure
): string {
  const split = getSplit(commitContext, row);

  if (!split) {
    return JSON.stringify(row.assignment);
  }

  const labeledAssignment = Object.fromEntries(
    Object.entries(row.assignment).map(([dimensionId, dimensionAssignment]) => {
      const dimension = split.dimensions[dimensionId] ?? undefined;

      if (!dimension) {
        return [dimensionId, dimensionAssignment];
      }

      return [
        dimension.name,
        dimension.type === "discrete" && dimensionAssignment.type === "discrete"
          ? (dimension.arms[dimensionAssignment.armId]?.name ??
            dimensionAssignment.armId)
          : dimensionAssignment,
      ];
    })
  );

  const result =
    Object.keys(labeledAssignment).length === 1
      ? Object.values(labeledAssignment)[0]
      : labeledAssignment;

  return JSON.stringify(result);
}
